import React, { useState, useCallback, useEffect, useRef } from 'react';
import DeckGL from '@deck.gl/react';
import {LightingEffect, AmbientLight, _SunLight as SunLight, DirectionalLight, FlyToInterpolator} from '@deck.gl/core';
import {TextLayer, GeoJsonLayer, PolygonLayer} from '@deck.gl/layers';
import { SimpleMeshLayer, ScenegraphLayer } from '@deck.gl/mesh-layers';
import {IcoSphereGeometry} from '@luma.gl/engine';
import {fitBounds} from '@math.gl/web-mercator';
import {StaticMap, AttributionControl} from 'react-map-gl';
import * as geom from './geom';

const dataURL = {
    'CHICAGO': {
        buildings: `${process.env.PUBLIC_URL}/data/chicagoBuildings.geojson`,
        sidewalks: `${process.env.PUBLIC_URL}/data/sidewalks.geojson`,
        trees: `${process.env.PUBLIC_URL}/data/chicagoTrees.json`,
    },
    'DC': {
        buildings: `${process.env.PUBLIC_URL}/data/dcBuildings.geojson`,
        sidewalks: `${process.env.PUBLIC_URL}/data/sidewalks.geojson`,
        trees: `${process.env.PUBLIC_URL}/data/dcTrees.json`,
    },
    'STLOUIS': {
        buildings: `${process.env.PUBLIC_URL}/data/stlBuildings.geojson`,
        sidewalks: `${process.env.PUBLIC_URL}/data/sidewalks.geojson`,
        trees: `${process.env.PUBLIC_URL}/data/stlTrees.json`,
    },
    'HOUSTON': {
        buildings: `${process.env.PUBLIC_URL}/data/hstnBuildings.geojson`,
        sidewalks: ``,
        trees: ``,
    },
    'NYC': {
        buildings: `${process.env.PUBLIC_URL}/data/nycBuildings.geojson`,
        sidewalks: ``,
        trees: ``,
    },
}

const landCover = {
    'CHICAGO': [[[-87.64524, 41.89274], [-87.59920, 41.89473], [-87.59544, 41.83836], [-87.64841, 41.84169]]],
    'DC': [[[-77.05721, 38.90616], [-77.05913, 38.86891], [-77, 38.88246], [-77, 38.90604]]], 
    'STLOUIS':[[[-90.342,38.7686],[-90.342,38.4297],[-90.1568,38.4297],[-90.1568, 38.7686]]],
    'HOUSTON':[[[-95.450077,29.736806],[-95.328884,29.736806],[-95.328884,29.791196],[-95.450077,29.791196]]],
    'NYC':[[[-74.056091,40.699121],[-74.056091,40.846801],[-73.882027,40.846801],[-73.882027,40.699121]]]
}

const attributions = {
    'CHICAGO': '© Mapbox, OpenStreetMap Contributors. Maxar. City of Chicago Open Data.',
    'NYC': '© Mapbox, OpenStreetMap Contributors. Maxar. New York City Open Data.',
    'DC': '© Mapbox, OpenStreetMap Contributors. Maxar',
    'STLOUIS': '© Mapbox, OpenStreetMap Contributors. Maxar',
    'HOUSTON': '© Mapbox, OpenStreetMap Contributors. Maxar',
}

const lightDirection = {
    'CHICAGO': [-60, 240, -120],
    'DC': [15, 55, -65],
    'STLOUIS': [-60, 70, -90],
    'HOUSTON': [-60, 70, -60],
    'NYC': [-90, 70, -240],
}

const getBounds = (width, height, bounds) => fitBounds({width, height, bounds})

const ambientLight = new AmbientLight({
    color: [255,255,255],
    intensity: 1.0
});
  
// const dirLight = new SunLight({
//     timestamp: Date.UTC(2019, 7, 1, 13),
//     color: [255, 255, 255],
//     intensity: 1.0,
//     _shadow: true
// });
  

const sphere = new IcoSphereGeometry({
    iterations: 1
});

function createContext(width, height) {
    var canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas.getContext("2d");
}

function mapScale(){
    return (1+(Math.log(2560/window.innerWidth)/10))
}

const mapOn = false;

const generatePoints = (polygons, holes, bounds, count, precision=6, predicate='inside') => {
    const scale = 10**precision;
    const letters = 'abcdefghijklmnopqrstuvwxyz';
    const bbox = bounds.map(coord => Math.round(coord*scale));
    const originX = bbox[0];
    const originY = bbox[1];
    const width = bbox[2]-bbox[0];
    const height = bbox[3]-bbox[1];
    var ctx = createContext(width, height)

    for (let i=0;i<polygons.length;i++){
        ctx.beginPath();
        ctx.fillStyle = "rgba(255, 0, 0, 1)";
        ctx.moveTo(Math.round(polygons[i][0][0]*scale)-originX,Math.round(polygons[i][0][1]*scale)-originY);
        for (let n=1; n<polygons[i].length;n++){
        ctx.lineTo(Math.round(polygons[i][n][0]*scale)-originX,Math.round(polygons[i][n][1]*scale)-originY);
        }
        ctx.fill()
    };

    for (let i=0;i<holes.length;i++){
        ctx.beginPath();
        ctx.fillStyle = "rgba(0, 0, 0, 1)";
        ctx.moveTo(Math.round(holes[i][0][0]*scale)-originX,Math.round(holes[i][0][1]*scale)-originY);
        for (let n=1; n<holes[i].length;n++){
        ctx.lineTo(Math.round(holes[i][n][0]*scale)-originX,Math.round(holes[i][n][1]*scale)-originY);
        }
        ctx.fill()
    };

    var canvasData = ctx.getImageData(0, 0, width, height);
    var returnArray = [];

    const pixels = {
        check: predicate === 'outside'? 
        (x,y,n) => {
            var index = (x + y * width) * 4;
            if (canvasData.data[index]!==255) returnArray.push({coords:[(x+originX)/scale+Math.random()/scale,(y+originY)/scale+Math.random()/scale], index: n, letter:letters[n%26]})
        } : (x,y,n) => {
            var index = (x + y * width) * 4;
            if (canvasData.data[index]===255) returnArray.push({coords:[(x+originX)/scale+Math.random()/scale,(y+originY)/scale+Math.random()/scale], index: n, letter:letters[n%26]})
        }
    }

    while (returnArray.length < count){
        pixels.check(Math.floor(Math.random()*width),Math.floor(Math.random()*height), returnArray.length)
    };
    return returnArray;
}

export default function Map( props ) {
    
    const { city, sumList, currIndex } = props;
    const [pointData, setPointData] = useState([])
    const [currSteps, setCurrSteps] = useState([])
    const [currLayers, setCurrLayers] = useState({})
    const [initialViewState, setInitialViewState] = useState({
        latitude:0,
        longitude:0,
        bearing:0,
        pitch:0,
        zoom:0
    })

    const cityLayers = {
        'CHICAGO': [
            new ScenegraphLayer({
              id: 'scenegraph-layer-CHI',
              data: [[-87.623333,41.8826875]],
              pickable: false,
              scenegraph: `${process.env.PUBLIC_URL}/data/bean.glb`,
              // scenegraph:'https://raw.githubusercontent.com/KhronosGroup/glTF-Sample-Models/master/2.0/BoxAnimated/glTF-Binary/BoxAnimated.glb',
              getPosition: d => d,
              // getOrientation: d => [0, Math.random() * 180, 90],
              sizeScale:0.2,
              getTranslation:[-10, 0, -1],
              getColor: [240,240,240,255],
              updateTriggers: {
                  data: city,
              }
            })],
        'STLOUIS':[
            new ScenegraphLayer({
              id: 'scenegraph-layer-STL',
              data: [[-90.185, 38.62465]],
              pickable: false,
              scenegraph: `${process.env.PUBLIC_URL}/data/stLouisArch.glb`,
              getPosition: d => d,
              getOrientation: d => [0, 71, 90],
              sizeScale:6.4,
              getTranslation:[0, 0, 0],
              getColor: [240,240,240,255],
              getScale: [1,1,0.4],
              updateTriggers: {
                  data: city,
              }
            })],
            
        'DC':[],
        'HOUSTON':[],
        'NYC':[]
    }

    const generateStepLayers = async (steps) => {
        let returnObj = {};
        
        for (let i=0; i<steps.length; i++){
            if (steps[i].dots.polygons.length && steps[i].dots.count>0){
                const points = generatePoints(
                    steps[i].dots.polygons,
                    steps[i].dots.cutouts,
                    steps[i].dots.bounds,
                    steps[i].dots.count,
                    steps[i].dots.precision||5,
                    steps[i].dots.predicate||'inside'
                )
                returnObj[i] = new TextLayer({
                    id: 'text-layer',
                    data: points,
                    pickable: false,
                    getPosition: d => [d.coords[0],d.coords[1],1],
                    getText: d => d.letter,
                    getColor: [255,255,0,255],
                    getSize: 4,
                    getAngle: 0,
                    getTextAnchor: 'middle',
                    getAlignmentBaseline: 'center',
                    sizeUnits: 'meters',
                    fontFamily: 'weepeople'
                })
            }
        }
        return returnObj;
    }

    const loadCity = async (steps) => {
        const pointLayers = await generateStepLayers(steps)
        
        setCurrSteps(steps)
        setCurrLayers(pointLayers)

    }

    useEffect(() => {
        loadCity(geom[city].steps)
    },[city])

    const updateMapLocation = async () => {
        const view = await currSteps[currIndex].view;
        setInitialViewState({
            ...view,
            zoom: view.zoom/mapScale(),
            transitionDuration: currIndex ? 1500 : 0,
            transitionInterpolator: new FlyToInterpolator()
        })    
    }

    useEffect(() => {
        if (currSteps[currIndex] !== undefined) updateMapLocation();
    },[currIndex, currSteps])

    const DeckRef = useRef(null)

    // useEffect(() => {
    //     generatePoints(geom[city].polygons, geom[city].cutouts, geom[city].bounds, sumList.slice(-1,)[0], resolution[city], 'inside')
    // },[city, sumList])

    const dirLight = new DirectionalLight({
        color: [255, 255, 255],
        intensity: 2.0,
        _shadow: true,
        direction: lightDirection[city]
    });

    const [effects] = useState(() => {
        const lightingEffect = new LightingEffect({ambientLight, dirLight});
        lightingEffect.shadowColor = [0, 0, 0, 0.5];
        return [lightingEffect];
    });

    const layers = [
      new PolygonLayer({
        id: 'ground',
        data: landCover[city],
        stroked: false,
        getPolygon: f => f,
        getFillColor: [0,0,0,0]
      }),
    //   new GeoJsonLayer({
    //     id: 'sidewalks-layer',
    //     data: dataURL[city].sidewalks,
    //     pickable: false,
    //     stroked: false,
    //     filled: true,
    //     extruded: true,
    //     getFillColor: [150, 150, 150],
    //     getElevation: 0.25,
    //     wireframe:true,
    //     material:false
    //   }),
      new GeoJsonLayer({
        id: 'buildings-layer',
        data: dataURL[city].buildings,
        pickable: false,
        stroked: false,
        filled: true,
        extruded: true,
        getFillColor: [240, 240, 240,255],
        getElevation: d => d.properties.HEIGHT/3.28084,
        wireframe:false,
        material:true,
        updateTriggers: {
          getElevation: city,
        }
      }),
      new TextLayer({
        id: 'text-layer',
        data: pointData,
        pickable: false,
        getPosition: d => [d.coords[0],d.coords[1],1],
        getText: d => d.letter,
        getColor: d => d.index <= sumList[currIndex] ? [255,255,0,255] :  [0,0,0,0],
        getSize: 4,
        getAngle: 0,
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center',
        sizeUnits: 'meters',
        fontFamily: 'weepeople',
        updateTriggers: {
          getColor: currIndex,
        }
      }),
      new SimpleMeshLayer({
        id: 'trees layer',
        data: dataURL[city].trees,
        getPosition: d => d,
        sizeScale: 1,
        opacity: 0.4,
        pickable:false,
        getScale: [2,2,3],
        mesh: sphere,
        getColor: [50, 200, 120],
        getTranslation: d => [0,0,1.5],
        updateTriggers: {
            data: city,
        }
      }),
    ]

    const renderLayers = (city, currIndex, currLayers) => {
        if (!Object.keys(currLayers).length){
            return [...layers, ...cityLayers[city]]
        } else {
            let returnLayers = [...layers, ...cityLayers[city]]
            if (currLayers[currIndex]) returnLayers.push(currLayers[currIndex])
            return returnLayers
        }
    }

    return (
        <div id="map-container" className={props.faded ? 'faded' : ''}>
            <DeckGL 
                initialViewState={initialViewState}
                // onViewStateChange={viewstate => console.log(viewstate)}
                controller={{dragRotate: mapOn, dragPan: mapOn, doubleClickZoom: mapOn, touchZoom: mapOn, touchRotate: mapOn, keyboard: mapOn, scrollZoom: mapOn}}
                layers={renderLayers(city, currIndex, currLayers)}
                effects={effects}
                ref={DeckRef}
                onLoad={() => console.log('loaded')}
                // onViewStateChange={viewState => {
                //     console.clear();
                //     console.log({
                //         'latitude':viewState?.viewState.latitude,
                //         'longitude':viewState?.viewState.longitude,
                //         'pitch':viewState?.viewState.pitch,
                //         'bearing':viewState?.viewState.bearing,
                //         'zoom':viewState?.viewState.zoom
                //     })
                //     }
                // }
            >     
                <StaticMap
                    reuseMaps
                    mapStyle={'mapbox://styles/dhalpern/ckllnfhbq1i4517myi50eii6f'}
                    preventStyleDiffing={true}
                    mapboxApiAccessToken={'pk.eyJ1IjoiZGhhbHBlcm4iLCJhIjoiY2p3MHFvZHg2MDcyczQ4bXBjNW85aDh2OCJ9.OUluk6vAGe5BVXLOiGIoQQ'}
                    attributionControl={false}
                >
                </StaticMap>
                <div id="attribution">{attributions[city]}</div>
            </DeckGL>
        </div>
    );
}